import { useState, useCallback, useMemo, useEffect } from 'react';

import { supabase } from '../../../lib/supabase';
import { FillBlankAnswer, OrderingAnswer, TrueFalseAnswer, UserAnswer } from '../../../types/questionFormats';
import { useUserProfile } from '../../quiz-core/useStore';
import { useLocalMultiplayerQuiz } from '../hooks/useLocalMultiplayerQuiz';

type MCQMultipleOption = {
  correct?: boolean;
};

export function useMultiplayerQuizController({
  sessionId,
  roomId,
  isHost: _isHost = false,
  reviewMode: _reviewMode = false,
  routeDifficulty,
  playerName: routePlayerName,
  userId: routeUserId,
}: {
  sessionId: string;
  roomId: string;
  isHost?: boolean;
  reviewMode?: boolean;
  routeDifficulty?: string;
  playerName?: string;
  userId?: string;
}) {
  const userProfile = useUserProfile();

  const effectiveUserId = useMemo(() => userProfile?.id || routeUserId, [userProfile, routeUserId]);
  const effectivePlayerName = useMemo(() => userProfile?.name || routePlayerName || 'Player', [userProfile, routePlayerName]);

  const [selectedAnswer, setSelectedAnswer] = useState<string | number[] | Record<string, string> | null>(null);
  const [showExplanation, setShowExplanation] = useState<boolean>(false);
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState<boolean>(false);
  const [isQuestionSubmitted, setIsQuestionSubmitted] = useState<boolean>(false);
  const [isSubmittingResults, setIsSubmittingResults] = useState<boolean>(false);
  const [isNavigating] = useState<boolean>(false);
  const [isWaitingForResults, setIsWaitingForResults] = useState<boolean>(false);
  const [canNavigateToResults, setCanNavigateToResults] = useState(false);
  const [waitingScreenShownAt, setWaitingScreenShownAt] = useState<number | null>(null);

  const difficultyTimeMap: Record<string, number> = {
    easy: 300,
    medium: 200,
    hard: 100,
  };

  const normalizedDifficulty = (routeDifficulty || 'easy').toLowerCase();
  const overallGameTimeSeconds = difficultyTimeMap[normalizedDifficulty] || 300;

  const localQuiz = useLocalMultiplayerQuiz({
    sessionId,
    userId: effectiveUserId,
    playerName: effectivePlayerName,
    initialQuestions: [],
    overallGameTimeSeconds,
    roomId, // Pass the roomId prop!
  });

  const { currentQuestion, questions, currentQuestionIndex, isGameFinished, submitResultsToServer, score, correctCount } = localQuiz || {};
  const currentIndex = useMemo(() => currentQuestionIndex ?? 0, [currentQuestionIndex]);
  const totalQuestions = useMemo(() => questions?.length || 0, [questions]);

  // Show waiting for results immediately after game finishes, until navigation is allowed
  useEffect(() => {
    if (isGameFinished && !canNavigateToResults) {
      setIsWaitingForResults(true);
    } else {
      setIsWaitingForResults(false);
    }
  }, [isGameFinished, canNavigateToResults]);

  // Set waitingScreenShownAt when game is finished (for navigation timing)
  useEffect(() => {
    if (isGameFinished && waitingScreenShownAt === null) {
      setWaitingScreenShownAt(Date.now());
      console.log('[useMultiplayerQuizController] waitingScreenShownAt set:', Date.now());
    }
  }, [isGameFinished, waitingScreenShownAt]);

  const toggleExplanation = useCallback(() => {
    setShowExplanation(prev => !prev);
  }, []);

  const handleAnswerSelect = useCallback((answer: string | UserAnswer) => {
    if (isAnswerSubmitted || isQuestionSubmitted || !currentQuestion) return;
    
    // Check if this is MCQ Multiple format
    const isMCQMultiple = currentQuestion.question_type === 'mcq_multiple' || 
                         (typeof answer === 'object' && answer.format === 'mcq_multiple');
    
    if (isMCQMultiple) {
      // For MCQ Multiple: Store as array, same as Single Player
      console.log('[MultiplayerQuizController] MCQ Multiple selection update:', answer);
      
      if (typeof answer === 'object' && answer.format === 'mcq_multiple') {
        // Store the answers array directly, not as a string
        setSelectedAnswer(answer.answers);
        
        // Check if this is a final submission (user has selected the exact required number)
        const multipleOptions = Array.isArray(currentQuestion.format_data?.options)
          ? (currentQuestion.format_data.options as MCQMultipleOption[])
          : [];
        const requiredSelections = multipleOptions.filter((opt) => opt.correct).length || 1;
        if (answer.answers.length >= requiredSelections) {
          // This is a final submission, proceed with normal flow
          setIsQuestionSubmitted(true);
          setIsAnswerSubmitted(true);
          setShowExplanation(true);
          localQuiz.selectAnswer(String(currentQuestion.id), answer);
        }
      }
    } else {
      // For all other formats: Immediate submission (existing behavior)
      setIsQuestionSubmitted(true);
      setIsAnswerSubmitted(true);
      setShowExplanation(true);
      
      if (typeof answer === 'string') {
        setSelectedAnswer(answer);
        localQuiz.selectAnswer(String(currentQuestion.id), answer);
      } else {
        if (answer.format === 'true_false') {
          setSelectedAnswer(String((answer as TrueFalseAnswer).answer));
        } else if (answer.format === 'fill_blank') {
          setSelectedAnswer((answer as FillBlankAnswer).answers);
        } else if (answer.format === 'ordering') {
          setSelectedAnswer((answer as OrderingAnswer).order);
        } else {
          setSelectedAnswer(convertUserAnswerToString(answer));
        }
        localQuiz.selectAnswer(String(currentQuestion.id), answer);
      }
    }
  }, [isAnswerSubmitted, isQuestionSubmitted, currentQuestion, localQuiz]);

  // Helper function to convert UserAnswer to string for backward compatibility
  const convertUserAnswerToString = useCallback((answer: UserAnswer): string => {
    switch (answer.format) {
      case 'mcq_single':
        return answer.answer;
      case 'true_false':
        return answer.answer.toString();
      case 'mcq_multiple':
        return answer.answers.join(',');
      case 'fill_blank':
        return Object.values(answer.answers).join(' | ');
      case 'ordering':
        return answer.order.join(',');
      default:
        return JSON.stringify(answer);
    }
  }, []);

  const handleNextQuestion = useCallback(() => {
    if (!isAnswerSubmitted) return;
    if (isGameFinished) {
      handleQuizCompletion();
    } else {
      localQuiz?.handleNextQuestion();
      setSelectedAnswer(null);
      setShowExplanation(false);
      setIsAnswerSubmitted(false);
      setIsQuestionSubmitted(false);
    }
  }, [isAnswerSubmitted, isGameFinished, localQuiz]);

  const handleQuizCompletion = useCallback(async () => {
    try {
      await submitResultsToServer();
    } catch (e) {
      console.error('Error submitting results:', e);
      return;
    }

    if (isSubmittingResults) return;

    try {
      setIsSubmittingResults(true);

      // First check if room is already completed (timer may have expired)
      const { data: roomData } = await supabase
        .from('multiplayer_game_rooms')
        .select('status')
        .eq('id', roomId)
        .single();
      
      if (roomData?.status === 'completed') {
        console.log('[handleQuizCompletion] Room already completed, game ended by timer');
        // Room is already completed, timer must have expired
        // Don't navigate to waiting screen, the room monitor will handle navigation
        setIsSubmittingResults(false);
        return;
      }

      const { error } = await supabase.rpc('maybe_complete_multiplayer_session', {
        p_session_id: sessionId,
        p_user_id: effectiveUserId,
      });

      if (error) {
        console.error('RPC Error:', error);
        return;
      }

      // setIsWaitingForResults(true); // No longer needed, handled by useEffect above
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmittingResults(false);
    }
  }, [submitResultsToServer, isSubmittingResults, sessionId, effectiveUserId, roomId]);

  const handleAutoSubmit = useCallback(() => {
    if (!isQuestionSubmitted && !isGameFinished && !isSubmittingResults && !isNavigating) {
      setIsQuestionSubmitted(true);
      handleQuizCompletion();
    }
  }, [isQuestionSubmitted, isGameFinished, isSubmittingResults, isNavigating]);

  return {
    state: {
      selectedAnswer,
      showExplanation,
      isAnswerSubmitted,
      isQuestionSubmitted,
      isSubmittingResults,
      isNavigating,
      isWaitingForResults,
      canNavigateToResults,
      waitingScreenShownAt,
    },
    handlers: {
      toggleExplanation,
      handleAnswerSelect,
      handleNextQuestion,
      handleQuizCompletion,
      handleAutoSubmit,
      setCanNavigateToResults,
      setWaitingScreenShownAt,
    },
    derived: {
      effectiveUserId,
      effectivePlayerName,
      currentQuestion,
      currentIndex,
      totalQuestions,
      localQuiz,
      isGameFinished,
      isInitialized: localQuiz.isInitialized,
      questions,
      score,
      correctCount,
      gracePeriodEnd: null,
    },
  };
}
